const portal = {
  en: {
    nav: {
      aiTools: 'AI Tools',
      analytics: 'Analytics',
      history: 'History',
      teams: 'Teams',
      apiKeys: 'API Keys',
      billing: 'Billing',
      reports: 'Reports',
      settings: 'Settings',
      logout: 'Logout',
    },
    welcome: {
      title: (name) => `Welcome back, ${name}!`,
      subtitle: 'I am Emma, your personal AI assistant — here to help your business move forward. Pick a tool and we will get started.',
    },
    portalTools: [
      { id: 'roadmap', title: 'AI Strategy & Roadmap', description: 'Create a comprehensive AI implementation roadmap tailored to your business goals.' },
      { id: 'agent', title: 'AI Agent Builder', description: 'Design and configure intelligent AI agents with our no-code workflow builder.' },
      { id: 'process', title: 'Process Optimizer', description: 'Analyze and optimize your business processes with AI-powered recommendations.' },
      { id: 'training', title: 'Virtual Training', description: 'Generate personalized AI training programs for your team.' },
    ],
    openTool: 'Open Tool',
    backToPortal: 'Back to Portal',
    aiToolsTitle: 'AI Compass for SMB',
    aiToolsWelcome: (name) => `Welcome, ${name}`,
    aiToolsSubtitle: (count) => `Emma here — ${count} tools ready to help. Where shall we start?`,
    categories: {
      all: 'All Tools',
      learn: 'Learn',
      analyze: 'Analyze',
      optimize: 'Optimize',
      grow: 'Grow',
      strategy: 'Strategy',
    },
    tools: {
      coach: { title: 'AI Coach', desc: 'Role-specific AI mentor with micro-lessons and hands-on challenges.' },
      explorer: { title: 'AI Insight Explorer', desc: 'Upload data and uncover hidden patterns with AI-powered visual analysis.' },
      automation: { title: 'AI Automation Assistant', desc: 'Detect repetitive tasks and get automation recipes with ROI estimates.' },
      simulator: { title: 'AI Decision Simulator', desc: 'Test business decisions safely with AI-predicted outcomes.' },
      cashflow: { title: 'AI Cashflow Predictor', desc: 'Predict your cashflow and prevent liquidity problems with AI.' },
      hiring: { title: 'AI Hiring Assistant', desc: 'Match CVs to roles, compare candidates, and find the perfect hire.' },
      sales: { title: 'AI Sales Assistant', desc: 'Lead scoring, deal prediction, and email drafting for your sales team.' },
      knowledge: { title: 'AI Knowledge Brain', desc: 'Turn company documents into a searchable AI assistant.' },
      pulse: { title: 'AI Customer Pulse', desc: 'Analyze customer sentiment and reveal feedback patterns.' },
      radar: { title: 'AI Opportunity Radar', desc: 'Discover hidden growth opportunities in your business data.' },
      mapper: { title: 'AI Process Mapper', desc: 'Visualize company workflows and detect bottlenecks.' },
      risk: { title: 'AI Risk Detector', desc: 'Detect financial and operational risks early.' },
      pricing: { title: 'AI Pricing Optimizer', desc: 'Optimize pricing and margins with AI analysis.' },
      meeting: { title: 'AI Meeting Brain', desc: 'Turn meetings into clear actions, decisions, and tasks.' },
    },
    common: {
      send: 'Send',
      back: 'Back',
      aiTools: 'AI Tools',
      loading: 'Loading...',
      analyzing: 'Analyzing...',
      upload: 'Upload',
      newAnalysis: 'New Analysis',
      newChat: 'New Chat',
      comingSoon: 'Coming Soon',
      error: 'An error occurred. Please try again.',
    },
    toolPages: {
      coach: {
        title: 'AI Coach',
        selectRole: 'Select your role to get started',
        chatPlaceholder: 'Ask your AI Coach...',
        microLessons: 'Micro-Lessons',
        challenges: 'Challenges',
        suggestions: 'Suggestions',
        newSession: 'New Session',
      },
      explorer: {
        title: 'Insight Explorer',
        dropzoneText: 'Drop CSV or Excel file here, or click to browse',
        analyze: 'Analyze with AI',
        askPlaceholder: 'Ask a question about your data...',
        charts: 'Charts',
        summary: 'Summary',
        aiAnalysis: 'AI Analysis',
      },
      automation: {
        title: 'Automation Assistant',
        describeWorkflow: 'Describe your workflow',
        placeholder: 'Describe the repetitive tasks in your daily work...',
        analyzeBtn: 'Detect Automation Opportunities',
        templates: 'Quick Start Templates',
        chatPlaceholder: 'Ask about automation...',
      },
      simulator: {
        title: 'Scenario Simulator',
        describeScenario: 'Describe your scenario',
        placeholder: 'What business scenario do you want to simulate?',
        simulateBtn: 'Simulate Scenario',
        presets: 'Scenario Presets',
        chatPlaceholder: 'Ask about this scenario...',
      },
      sales: {
        title: 'AI Sales Assistant',
        uploadLeads: 'Upload leads CSV/Excel',
        orDescribe: '— or describe your leads —',
        analyzePlaceholder: 'Describe your leads or customer info...',
        analyzeBtn: 'Analyze Leads',
        draftEmail: 'Draft Follow-up Email',
        chatPlaceholder: 'Ask about your sales data...',
      },
      knowledge: {
        title: 'AI Knowledge Brain',
        uploadDocs: 'Upload company documents',
        addText: 'Add Text Document',
        askPlaceholder: 'Ask a question about your company knowledge...',
        documents: 'Documents',
      },
      pulse: {
        title: 'Customer Pulse',
        uploadFeedback: 'Upload feedback CSV/Excel',
        orPaste: '— or paste feedback —',
        analyzePlaceholder: 'Paste customer reviews, feedback, or survey results...',
        analyzeBtn: 'Analyze Customer Pulse',
        chatPlaceholder: 'Ask about customer feedback...',
      },
      radar: {
        title: 'Opportunity Radar',
        uploadData: 'Upload business data CSV/Excel',
        orDescribe: '— or describe your business data —',
        analyzePlaceholder: 'Describe your products, sales data, or market info...',
        analyzeBtn: 'Scan for Opportunities',
        chatPlaceholder: 'Ask about opportunities...',
      },
      mapper: {
        title: 'AI Process Mapper',
        describeProcess: 'Describe your business process',
        placeholder: 'Describe a workflow step by step...',
        mapBtn: 'Map & Analyze Process',
        templates: 'Process Templates',
        chatPlaceholder: 'Ask about this process...',
      },
      risk: {
        title: 'AI Risk Detector',
        uploadData: 'Upload business data CSV/Excel',
        orDescribe: '— or describe your business metrics —',
        analyzePlaceholder: 'Describe your financial metrics, operational data...',
        analyzeBtn: 'Detect Risks',
        chatPlaceholder: 'Ask about risks...',
      },
      pricing: {
        title: 'AI Pricing Optimizer',
        uploadData: 'Upload CSV/Excel with product pricing data',
        orDescribe: '— or describe your products —',
        analyzePlaceholder: 'E.g., Product A: price €45, cost €22, 200 units/month...',
        analyzeBtn: 'Analyze & Optimize Pricing',
        chatPlaceholder: 'Ask about pricing...',
      },
      meeting: {
        title: 'AI Meeting Brain',
        inputLabel: 'Transform Meetings into Actions',
        inputDesc: 'Paste meeting notes or transcript and AI extracts decisions, action items, and follow-ups.',
        analyzePlaceholder: 'Meeting Notes Example:\n\nTeam meeting - March 4, 2026\nAttendees: John, Sarah, Mike\n\nDiscussed Q2 marketing budget...',
        analyzeBtn: 'Extract Actions & Decisions',
        chatPlaceholder: 'Ask about the meeting...',
      },
      roadmap: {
        title: 'AI Strategy & Roadmap',
        intro: 'Create Your AI Implementation Roadmap',
        introDesc: "Answer a few questions about your business and we'll generate a comprehensive, actionable AI strategy tailored specifically for you.",
        companyName: 'Company Name',
        companyPlaceholder: 'Your Company B.V.',
        industry: 'Industry / Sector',
        industryPlaceholder: 'e.g., E-commerce, Manufacturing, Healthcare',
        challenges: 'Current Challenges',
        challengesPlaceholder: 'Describe your main business challenges and pain points...',
        goals: 'Goals & Objectives',
        goalsPlaceholder: 'What do you want to achieve with AI implementation?',
        timeline: 'Timeline',
        budget: 'Budget Range',
        generating: 'Generating Roadmap...',
        generate: 'Generate AI Roadmap',
        resultTitle: 'Your AI Implementation Roadmap',
        createNew: 'Create New Roadmap',
      },
      agent: {
        title: 'AI Agent Builder',
        intro: 'Design Your AI Agent',
        introDesc: 'Build custom AI agents with our no-code builder. Choose your preferred LLM model and configure behavior for your specific use case.',
        agentName: 'Agent Name',
        agentPlaceholder: 'e.g., Customer Support Assistant',
        description: 'Description',
        descPlaceholder: 'Brief description of what this agent does...',
        provider: 'Model Provider',
        model: 'Model',
        systemPrompt: 'System Prompt',
        systemPlaceholder: 'Define how the agent should behave, its personality, and guidelines...',
        useCase: 'Use Case / Scenario',
        useCasePlaceholder: 'Describe the specific scenarios where this agent will be used...',
        building: 'Building Agent...',
        build: 'Build AI Agent',
        resultTitle: 'Your AI Agent Design',
        buildAnother: 'Build Another Agent',
      },
      processOpt: {
        title: 'Process Optimizer',
        intro: 'Optimize Your Business Processes',
        introDesc: 'Analyze your current processes and get AI-powered recommendations for automation, efficiency improvements, and cost savings.',
        processName: 'Process Name',
        processPlaceholder: 'e.g., Customer Onboarding, Invoice Processing',
        currentProcess: 'Current Process Description',
        currentPlaceholder: 'Describe step-by-step how this process currently works...',
        painPoints: 'Pain Points & Challenges',
        painPlaceholder: 'What are the main issues, bottlenecks, or inefficiencies?',
        teamSize: 'Team Size Involved',
        frequency: 'Process Frequency',
        analyzing: 'Analyzing Process...',
        optimize: 'Optimize Process',
        resultTitle: 'Process Optimization Plan',
        analyzeAnother: 'Analyze Another Process',
      },
      virtualTraining: {
        title: 'Virtual Training',
        intro: 'Create Custom Training Programs',
        introDesc: 'Generate comprehensive, AI-powered training curricula tailored to your team\'s needs with interactive exercises and assessments.',
        topic: 'Training Topic',
        topicPlaceholder: 'e.g., AI Fundamentals, Data Analytics, Prompt Engineering',
        skillLevel: 'Target Skill Level',
        beginner: 'Beginner (No prior knowledge)',
        intermediate: 'Intermediate (Some experience)',
        advanced: 'Advanced (Expert level)',
        objectives: 'Learning Objectives',
        objectivesPlaceholder: 'What should participants be able to do after completing this training?',
        duration: 'Training Duration',
        teamSize: 'Team Size',
        generating: 'Generating Training...',
        generate: 'Generate Training Program',
        resultTitle: 'Your Training Program',
        createAnother: 'Create Another Program',
      },
    },
  },
  nl: {
    nav: {
      aiTools: 'AI Tools',
      analytics: 'Analyses',
      history: 'Geschiedenis',
      teams: 'Teams',
      apiKeys: 'API Sleutels',
      billing: 'Facturatie',
      reports: 'Rapporten',
      settings: 'Instellingen',
      logout: 'Uitloggen',
    },
    welcome: {
      title: (name) => `Welkom terug, ${name}!`,
      subtitle: 'Ik ben Emma, je persoonlijke AI-assistente — hier om je bedrijf vooruit te helpen. Kies een tool, dan gaan we aan de slag.',
    },
    portalTools: [
      { id: 'roadmap', title: 'AI Strategie & Roadmap', description: 'Maak een uitgebreid AI-implementatieplan op maat van je bedrijfsdoelen.' },
      { id: 'agent', title: 'AI Agent Builder', description: 'Ontwerp en configureer intelligente AI-agents met onze no-code workflow builder.' },
      { id: 'process', title: 'Procesoptimalisatie', description: 'Analyseer en optimaliseer je bedrijfsprocessen met AI-aanbevelingen.' },
      { id: 'training', title: 'Virtuele Training', description: 'Genereer gepersonaliseerde AI-trainingen voor je team.' },
    ],
    openTool: 'Open Tool',
    backToPortal: 'Terug naar Portaal',
    aiToolsTitle: 'AI Kompas voor MKB',
    aiToolsWelcome: (name) => `Welkom, ${name}`,
    aiToolsSubtitle: (count) => `Emma hier — ${count} tools klaar om je te helpen. Waar beginnen we?`,
    categories: {
      all: 'Alle Tools',
      learn: 'Leren',
      analyze: 'Analyseren',
      optimize: 'Optimaliseren',
      grow: 'Groeien',
      strategy: 'Strategie',
    },
    tools: {
      coach: { title: 'AI Coach', desc: 'Rolspecifieke AI-mentor met microlessen en praktijkopdrachten.' },
      explorer: { title: 'AI Insight Explorer', desc: 'Upload data en ontdek verborgen patronen met AI-analyse.' },
      automation: { title: 'AI Automation Assistant', desc: 'Detecteer repetitieve taken en ontvang automatiseringsrecepten met ROI-schattingen.' },
      simulator: { title: 'AI Decision Simulator', desc: 'Test zakelijke beslissingen veilig met AI-voorspelde uitkomsten.' },
      cashflow: { title: 'AI Cashflow Predictor', desc: 'Voorspel je cashflow en voorkom liquiditeitsproblemen met AI.' },
      hiring: { title: 'AI Hiring Assistant', desc: 'Match CVs aan functies, vergelijk kandidaten en vind de perfecte match.' },
      sales: { title: 'AI Sales Assistant', desc: 'Leadscoring, dealvoorspelling en e-maildrafting voor je salesteam.' },
      knowledge: { title: 'AI Knowledge Brain', desc: 'Maak van bedrijfsdocumenten een doorzoekbare AI-assistent.' },
      pulse: { title: 'AI Customer Pulse', desc: 'Analyseer klanttevredenheid en onthul feedbackpatronen.' },
      radar: { title: 'AI Opportunity Radar', desc: 'Ontdek verborgen groeikansen in je bedrijfsdata.' },
      mapper: { title: 'AI Process Mapper', desc: 'Visualiseer bedrijfsworkflows en detecteer knelpunten.' },
      risk: { title: 'AI Risk Detector', desc: 'Detecteer financiele en operationele risicos vroegtijdig.' },
      pricing: { title: 'AI Pricing Optimizer', desc: 'Optimaliseer prijzen en marges met AI-analyse.' },
      meeting: { title: 'AI Meeting Brain', desc: 'Zet vergaderingen om in duidelijke acties, besluiten en taken.' },
    },
    common: {
      send: 'Verstuur',
      back: 'Terug',
      aiTools: 'AI Tools',
      loading: 'Laden...',
      analyzing: 'Analyseren...',
      upload: 'Uploaden',
      newAnalysis: 'Nieuwe Analyse',
      newChat: 'Nieuw Gesprek',
      comingSoon: 'Binnenkort',
      error: 'Er is een fout opgetreden. Probeer het opnieuw.',
    },
    toolPages: {
      coach: {
        title: 'AI Coach',
        selectRole: 'Selecteer je rol om te beginnen',
        chatPlaceholder: 'Stel een vraag aan je AI Coach...',
        microLessons: 'Microlessen',
        challenges: 'Uitdagingen',
        suggestions: 'Suggesties',
        newSession: 'Nieuwe Sessie',
      },
      explorer: {
        title: 'Inzicht Verkenner',
        dropzoneText: 'Sleep een CSV- of Excel-bestand hierheen, of klik om te bladeren',
        analyze: 'Analyseer met AI',
        askPlaceholder: 'Stel een vraag over je data...',
        charts: 'Grafieken',
        summary: 'Samenvatting',
        aiAnalysis: 'AI Analyse',
      },
      automation: {
        title: 'Automatiseringsassistent',
        describeWorkflow: 'Beschrijf je workflow',
        placeholder: 'Beschrijf de repetitieve taken in je dagelijks werk...',
        analyzeBtn: 'Detecteer Automatiseringskansen',
        templates: 'Snel-Start Templates',
        chatPlaceholder: 'Vraag over automatisering...',
      },
      simulator: {
        title: 'Scenario Simulator',
        describeScenario: 'Beschrijf je scenario',
        placeholder: 'Welk bedrijfsscenario wil je simuleren?',
        simulateBtn: 'Simuleer Scenario',
        presets: 'Scenario Voorinstellingen',
        chatPlaceholder: 'Vraag over dit scenario...',
      },
      sales: {
        title: 'AI Verkoopassistent',
        uploadLeads: 'Upload leads CSV/Excel',
        orDescribe: '— of beschrijf je leads —',
        analyzePlaceholder: 'Beschrijf je leads of klantinformatie...',
        analyzeBtn: 'Analyseer Leads',
        draftEmail: 'Concepte-mail Schrijven',
        chatPlaceholder: 'Vraag over je verkoopdata...',
      },
      knowledge: {
        title: 'AI Kennisbrein',
        uploadDocs: 'Upload bedrijfsdocumenten',
        addText: 'Tekst Document Toevoegen',
        askPlaceholder: 'Stel een vraag over je bedrijfskennis...',
        documents: 'Documenten',
      },
      pulse: {
        title: 'Klantpuls',
        uploadFeedback: 'Upload feedback CSV/Excel',
        orPaste: '— of plak feedback —',
        analyzePlaceholder: 'Plak klantreviews, feedback of enqueteresultaten...',
        analyzeBtn: 'Analyseer Klantpuls',
        chatPlaceholder: 'Vraag over klantfeedback...',
      },
      radar: {
        title: 'Kansen Radar',
        uploadData: 'Upload bedrijfsdata CSV/Excel',
        orDescribe: '— of beschrijf je bedrijfsdata —',
        analyzePlaceholder: 'Beschrijf je producten, verkoopdata of marktinfo...',
        analyzeBtn: 'Scan naar Kansen',
        chatPlaceholder: 'Vraag over kansen...',
      },
      mapper: {
        title: 'AI Proces Mapper',
        describeProcess: 'Beschrijf je bedrijfsproces',
        placeholder: 'Beschrijf een workflow stap voor stap...',
        mapBtn: 'Map & Analyseer Proces',
        templates: 'Proces Templates',
        chatPlaceholder: 'Vraag over dit proces...',
      },
      risk: {
        title: 'AI Risico Detector',
        uploadData: 'Upload bedrijfsdata CSV/Excel',
        orDescribe: '— of beschrijf je bedrijfscijfers —',
        analyzePlaceholder: 'Beschrijf je financiele cijfers, operationele data...',
        analyzeBtn: 'Detecteer Risicos',
        chatPlaceholder: 'Vraag over risicos...',
      },
      pricing: {
        title: 'AI Prijsoptimalisatie',
        uploadData: 'Upload CSV/Excel met productprijsdata',
        orDescribe: '— of beschrijf je producten —',
        analyzePlaceholder: 'Bijv. Product A: prijs €45, kosten €22, 200 stuks/maand...',
        analyzeBtn: 'Analyseer & Optimaliseer Prijzen',
        chatPlaceholder: 'Vraag over prijzen...',
      },
      meeting: {
        title: 'AI Vergaderbrein',
        inputLabel: 'Zet Vergaderingen om in Acties',
        inputDesc: 'Plak vergadernotities of transcript en AI haalt besluiten, actiepunten en opvolging eruit.',
        analyzePlaceholder: 'Vergadernotities voorbeeld:\n\nTeamvergadering - 4 maart 2026\nAanwezigen: Jan, Sara, Mark\n\nBesproken Q2 marketingbudget...',
        analyzeBtn: 'Acties & Besluiten Extraheren',
        chatPlaceholder: 'Vraag over de vergadering...',
      },
      roadmap: {
        title: 'AI Strategie & Roadmap',
        intro: 'Maak je AI Implementatie Roadmap',
        introDesc: 'Beantwoord een paar vragen over je bedrijf en wij genereren een uitgebreide, actiegerichte AI-strategie op maat voor jou.',
        companyName: 'Bedrijfsnaam',
        companyPlaceholder: 'Je Bedrijf B.V.',
        industry: 'Branche / Sector',
        industryPlaceholder: 'bijv. E-commerce, Productie, Zorg',
        challenges: 'Huidige Uitdagingen',
        challengesPlaceholder: 'Beschrijf je belangrijkste zakelijke uitdagingen en knelpunten...',
        goals: 'Doelen & Doelstellingen',
        goalsPlaceholder: 'Wat wil je bereiken met AI-implementatie?',
        timeline: 'Tijdlijn',
        budget: 'Budgetrange',
        generating: 'Roadmap genereren...',
        generate: 'AI Roadmap Genereren',
        resultTitle: 'Je AI Implementatie Roadmap',
        createNew: 'Nieuwe Roadmap Maken',
      },
      agent: {
        title: 'AI Agent Builder',
        intro: 'Ontwerp je AI Agent',
        introDesc: 'Bouw aangepaste AI-agents met onze no-code builder. Kies je favoriete LLM-model en configureer het gedrag voor jouw specifieke toepassing.',
        agentName: 'Agent Naam',
        agentPlaceholder: 'bijv. Klantenservice Assistent',
        description: 'Beschrijving',
        descPlaceholder: 'Korte beschrijving van wat deze agent doet...',
        provider: 'Model Provider',
        model: 'Model',
        systemPrompt: 'Systeem Prompt',
        systemPlaceholder: 'Definieer hoe de agent zich moet gedragen, zijn persoonlijkheid en richtlijnen...',
        useCase: 'Toepassing / Scenario',
        useCasePlaceholder: 'Beschrijf de specifieke scenarios waarin deze agent wordt gebruikt...',
        building: 'Agent bouwen...',
        build: 'AI Agent Bouwen',
        resultTitle: 'Je AI Agent Ontwerp',
        buildAnother: 'Nog Een Agent Bouwen',
      },
      processOpt: {
        title: 'Procesoptimalisatie',
        intro: 'Optimaliseer je Bedrijfsprocessen',
        introDesc: 'Analyseer je huidige processen en krijg AI-aanbevelingen voor automatisering, efficientieverbetering en kostenbesparing.',
        processName: 'Procesnaam',
        processPlaceholder: 'bijv. Klant Onboarding, Facturatieproces',
        currentProcess: 'Huidig Procesbeschrijving',
        currentPlaceholder: 'Beschrijf stap voor stap hoe dit proces nu werkt...',
        painPoints: 'Knelpunten & Uitdagingen',
        painPlaceholder: 'Wat zijn de belangrijkste problemen, knelpunten of inefficienties?',
        teamSize: 'Teamgrootte',
        frequency: 'Procesfrequentie',
        analyzing: 'Proces analyseren...',
        optimize: 'Proces Optimaliseren',
        resultTitle: 'Proces Optimalisatie Plan',
        analyzeAnother: 'Ander Proces Analyseren',
      },
      virtualTraining: {
        title: 'Virtuele Training',
        intro: 'Maak Aangepaste Trainingen',
        introDesc: 'Genereer uitgebreide, AI-aangedreven trainingen op maat van je team met interactieve oefeningen en toetsen.',
        topic: 'Training Onderwerp',
        topicPlaceholder: 'bijv. AI Basis, Data Analyse, Prompt Engineering',
        skillLevel: 'Doelniveau',
        beginner: 'Beginner (Geen voorkennis)',
        intermediate: 'Gemiddeld (Enige ervaring)',
        advanced: 'Gevorderd (Expert niveau)',
        objectives: 'Leerdoelen',
        objectivesPlaceholder: 'Wat moeten deelnemers kunnen na het voltooien van deze training?',
        duration: 'Trainingsduur',
        teamSize: 'Teamgrootte',
        generating: 'Training genereren...',
        generate: 'Training Genereren',
        resultTitle: 'Je Trainingsprogramma',
        createAnother: 'Nog Een Training Maken',
      },
    },
  },
};

export default portal;
